<template>
  <NoticeToastHost>
    <NoticeToast
      v-for="toast in toasts"
      :key="toast.id"
      :variant="toast.variant"
      :title="toast.title"
      :dismiss-label="t('common.close')"
      @dismiss="dismiss(toast.id)"
    >
      {{ toast.message }}
      <template v-if="toast.retry" #action>
        <Button size="sm" variant="ghost" @click="() => { dismiss(toast.id); toast.retry?.(); }">{{ t('common.retry') }}</Button>
      </template>
      <template v-else-if="toast.action" #action>
        <Button size="sm" variant="ghost" @click="toast.action">
          {{ toast.actionLabel || t('common.retry') }}
        </Button>
      </template>
    </NoticeToast>
  </NoticeToastHost>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted } from 'vue';
import { NoticeToastHost, NoticeToast, Button } from '@lumi/ui';
import { useI18n } from 'vue-i18n';
import { useToast } from '../composables/useToast';

const { t } = useI18n();
const { toasts, dismiss } = useToast();

// #470: a toast closing on its own timer wasn't enough — a pointerdown
// anywhere on the page OUTSIDE every toast now dismisses all of them,
// errors (never timed out) included. A document-level CAPTURE-phase
// listener rather than a ref on the host: NoticeToastHost teleports to
// <body> on its own, so there is no in-tree element to anchor a scoped
// listener to, and its host class is the one stable hook every toast card
// renders inside.
function dismissAllOutside(e: PointerEvent) {
  const target = e.target as Node | null;
  const host = document.querySelector('.lumi-toast-host');
  // Inside a toast (its own dismiss/action button included): let the click
  // reach it untouched, never swallowed by this listener.
  if (host && target && host.contains(target)) return;
  // A snapshot, not the live reactive array: `dismiss` splices it, and
  // splicing the array a for-of is still iterating skips whatever shifted
  // into the index it was about to visit next.
  for (const toast of [...toasts.value]) {
    // Never dismiss the toast THIS pointer event just created (e.g. a click
    // elsewhere on the page that itself pushes a toast synchronously): a
    // capture-phase listener runs before any bubble-phase handler on the
    // same dispatch, so this only matters for a toast created by a capture
    // handler earlier in the tree — defensive, but cheap and explicit.
    if (toast.createdAt >= e.timeStamp) continue;
    dismiss(toast.id);
  }
}
onMounted(() => document.addEventListener('pointerdown', dismissAllOutside, true));
onBeforeUnmount(() => document.removeEventListener('pointerdown', dismissAllOutside, true));
</script>
